import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HeartHandshake, Menu, UserRound, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import LanguageSelector from "./LanguageSelector";
import VisitStrip from "../VisitStrip";
import "./Navbar.css";

const navItems = [
  { key: "navLinks.home", path: "/" },
  { key: "nav.impact", path: "/impact" },
  { key: "nav.programs", path: "/programs" },
  { key: "nav.vedant", path: "/vedant" },
  { key: "nav.reports", path: "/reports" },
  { key: "nav.upcomingEvent", path: "/upcoming-event" },
  { key: "navLinks.blogs", path: "/blogs" },
  { key: "navLinks.volunteer", path: "/volunteer" },
];

const Navbar = ({ announcement }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getTextStyle = () => "text-[#14201f] hover:text-[#9d1f1c]";

  const navClass = scrolled
    ? "border-[#dfe8e6] bg-white/95 shadow-sm backdrop-blur"
    : "border-transparent bg-white/95 backdrop-blur";

  const getNavLinkStyle = (path) => {
    const isActive = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
    return `transition hover:text-[#9d1f1c] ${isActive ? "text-[#9d1f1c]" : "text-[#14201f]"}`;
  };

  const items = announcement?.enabled
    ? [...navItems, { key: "nav.scheduleVisit", path: "/schedule", flash: true }]
    : navItems;

  return (
    <header
      className={`fixed left-0 top-0 z-50 w-full border-b transition duration-300 ${navClass}`}
    >
      <VisitStrip announcement={announcement} />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 max-w-full items-center justify-between gap-5 sm:max-w-none lg:h-24">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-3"
            aria-label={t("nav.homeAriaLabel")}
          >
            <img
              src="/assets/Avdheshanandg mission logo.png"
              alt={t("nav.logoAlt")}
              className="h-14 w-auto shrink-0 lg:h-16"
            />
            <div className="hidden min-w-0 sm:block">
              <p className="whitespace-nowrap text-[11px] font-bold uppercase leading-tight tracking-[0.16em] text-[#14201f] md:text-xs">
                AvdheshanandG
              </p>
              <p className="mt-0.5 whitespace-nowrap text-[11px] font-semibold uppercase leading-tight tracking-[0.22em] text-[#9d1f1c] md:text-xs">
                Mission
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-5 text-sm font-semibold text-[#14201f] xl:flex">
            {items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`inline-flex items-center ${getNavLinkStyle(item.path)}`}
              >
                {item.flash ? (
                  <span
                    className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#f5c45e]"
                    style={{ animation: "agmNavPulse 1.8s ease-in-out infinite" }}
                    aria-hidden="true"
                  />
                ) : null}
                {t(item.key)}
              </Link>
            ))}
            <style>{`@keyframes agmNavPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.45;transform:scale(.7)}}`}</style>
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <LanguageSelector scrolled={true} getTextStyle={getTextStyle} />
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/profile"
                  className="flex h-10 w-10 items-center justify-center rounded-md bg-[#f8efe5] text-sm font-bold text-[#9d1f1c]"
                  aria-label={t("nav.profileAriaLabel")}
                >
                  {user?.email?.charAt(0)?.toUpperCase() || <UserRound size={18} />}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-sm font-semibold text-[#60706d] transition hover:text-[#9d1f1c]"
                >
                  {t("rightSection.logout")}
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="inline-flex h-10 items-center gap-2 rounded-md border border-[#dfe8e6] px-3 text-sm font-semibold text-[#14201f] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
              >
                <UserRound size={17} />
                {t("rightSection.login")}
              </Link>
            )}
            <Link
              to="/donate"
              className="inline-flex h-11 items-center gap-2 rounded-md bg-[#9d1f1c] px-5 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
            >
              <HeartHandshake size={18} />
              {t("navLinks.donate")}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen((value) => !value)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-[#dfe8e6] text-[#14201f] xl:hidden"
            aria-label={isOpen ? t("nav.closeNav") : t("nav.openNav")}
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-[#dfe8e6] bg-white xl:hidden">
          <nav className="container mx-auto grid gap-2 px-4 py-5 text-sm font-semibold sm:px-6">
            {items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center rounded-md px-3 py-3 transition hover:bg-[#f1f6f4] hover:text-[#9d1f1c] ${getNavLinkStyle(item.path)}`}
              >
                {item.flash ? (
                  <span
                    className="mr-2 inline-block h-2 w-2 rounded-full bg-[#f5c45e]"
                    aria-hidden="true"
                  />
                ) : null}
                {t(item.key)}
              </Link>
            ))}
            <div className="flex items-center justify-between gap-3 border-t border-[#dfe8e6] pt-4">
              <LanguageSelector scrolled={true} getTextStyle={getTextStyle} />
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white"
              >
                <HeartHandshake size={18} />
                {t("navLinks.donate")}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
